/**
 * Tab 02 · Directions — the list of all eight.
 *
 * A direction the user has not taken shows a lock and a dashed bar, never an
 * approximate score: the routing block touches all eight, but five screening
 * questions across eight domains is a recommendation signal, not a measurement.
 *
 * Mobile is a plain stacked list. Desktop is the handoff's `DDirs`: one panel of
 * fixed-column rows plus a 320px rail that explains the direction/pattern split
 * and surfaces the weakest direction — the two questions this screen actually
 * gets asked.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { useTt } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent } from './content';
import { useProfile } from './profile';
import { Icon } from './icons';
import {
    Appear,
    Col,
    Delta,
    Fluid,
    H1,
    Meta,
    Muted,
    Panel,
    Rail,
    Row,
    SegBarC,
    SegBarEmpty,
    useIsDesktop,
} from './primitives';
import { c, hair, hair2, f, ACT, scoreColor, DIR_ORDER, DESKTOP } from './tokens';
import { R, QUIZ } from './routes';

const DirectionsPage: React.FC = () => {
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);

    const done = profile.measuredTracks.length;
    const weakest = [...profile.directions].filter((d) => d.deep).sort((a, b) => (a.score ?? 0) - (b.score ?? 0))[0];
    const weakestDir = weakest ? content?.dirBy[weakest.code] : null;

    const intro = t('app.dirs.intro');
    const outro = t('app.dirs.outro');

    const shellProps = {
        active: 'dirs' as const,
        title: t('app.dirs.title'),
        subtitle: t('app.dirs.subtitle', { n: done }),
        index: profile.index,
        email: userProfile?.email || user?.email,
        chips:
            done < 8 ? (
                <Link to={QUIZ.full} style={{ textDecoration: 'none' }}>
                    <Row style={{ gap: 9, padding: '7px 13px', borderRadius: 99, border: hair, background: c.card }}>
                        <Meta style={{ fontSize: 10, color: c.textPri }}>{t('app.dirs.takeRest')}</Meta>
                        <Icon k="arrow" size={13} color={c.textPri} />
                    </Row>
                </Link>
            ) : (
                <Meta style={{ fontSize: 10, border: hair, borderRadius: 99, padding: '6px 10px' }}>
                    {t('app.dirs.allTaken')}
                </Meta>
            ),
    };

    /* ── desktop ─────────────────────────────────────── */
    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DeskRow>
                        <Fluid>
                            <Muted style={{ fontSize: 13.5, maxWidth: 620 }}>{intro}</Muted>

                            <Panel pad="6px 24px 18px">
                                {DIR_ORDER.map((code, i) => {
                                    const d = content?.dirBy[code];
                                    const st = profile.directions.find((x) => x.code === code);
                                    const deep = !!st?.deep;
                                    const z = scoreColor(deep ? st?.score : null);
                                    const pats = st?.patterns.length || 0;

                                    if (!d) return null;

                                    return (
                                        <Link
                                            key={code}
                                            to={R.dir(code)}
                                            style={{ textDecoration: 'none', display: 'block' }}
                                        >
                                            <Row
                                                style={{
                                                    gap: 20,
                                                    padding: '17px 0',
                                                    borderTop: i ? hair2 : 'none',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Meta
                                                    style={{
                                                        fontSize: 10,
                                                        color: c.subtle,
                                                        width: 18,
                                                        flex: '0 0 auto',
                                                    }}
                                                >
                                                    {String(i + 1).padStart(2, '0')}
                                                </Meta>
                                                <Icon
                                                    k={deep ? code : 'lock'}
                                                    size={18}
                                                    color={deep ? z.hex : 'rgba(232,230,224,0.3)'}
                                                />
                                                <Col style={{ width: 150, flex: '0 0 auto', minWidth: 0 }} gap={5}>
                                                    <Row style={{ gap: 9 }}>
                                                        <span
                                                            style={{
                                                                fontSize: 17,
                                                                fontWeight: 400,
                                                                letterSpacing: '-0.022em',
                                                                color: deep ? c.textPri : c.dim,
                                                            }}
                                                        >
                                                            {d.disp}
                                                        </span>
                                                        {deep && st?.delta != null && <Delta v={st.delta} />}
                                                    </Row>
                                                    <Meta
                                                        style={{
                                                            fontSize: 9,
                                                            letterSpacing: '0.1em',
                                                            color: deep ? z.hex : c.subtle,
                                                        }}
                                                    >
                                                        {t(deep ? z.key : 'app.zone.locked')}
                                                    </Meta>
                                                </Col>
                                                <Muted
                                                    style={{
                                                        fontSize: 13,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        color: deep ? c.muted : c.subtle,
                                                    }}
                                                >
                                                    {d.sub}
                                                </Muted>
                                                <Col style={{ width: 210, flex: '0 0 auto' }} gap={7}>
                                                    {deep ? (
                                                        <Row style={{ gap: 11 }}>
                                                            <SegBarC pct={st?.score || 0} hex={z.hex} n={10} />
                                                            <span
                                                                style={{
                                                                    fontFamily: f.mono,
                                                                    fontSize: 15,
                                                                    fontWeight: 300,
                                                                    color: c.textPri,
                                                                    width: 22,
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {st?.score}
                                                            </span>
                                                        </Row>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                height: 5,
                                                                borderRadius: 2,
                                                                border: '1px dashed rgba(232,230,224,0.14)',
                                                            }}
                                                        />
                                                    )}
                                                    <Meta
                                                        style={{
                                                            fontSize: 9,
                                                            letterSpacing: '0.1em',
                                                            color: c.subtle,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {deep
                                                            ? tn('app.state.patternCount', pats)
                                                            : t('app.dirs.sixMinutes')}
                                                        {!!st?.earned && (
                                                            <span style={{ color: ACT }}>
                                                                {' '}· +{tn('app.state.points', st.earned)}
                                                            </span>
                                                        )}
                                                    </Meta>
                                                </Col>
                                                <Icon k="chev" size={13} color={c.subtle} />
                                            </Row>
                                        </Link>
                                    );
                                })}
                            </Panel>

                            <Muted style={{ fontSize: 12 }}>{outro}</Muted>
                        </Fluid>

                        <Rail width={320}>
                            <Panel title={t('app.dirs.whatIs')} pad="16px 18px 18px">
                                <Muted style={{ fontSize: 12.5 }}>
                                    <span style={{ color: c.text }}>{t('app.dirs.whatIs.lead')}</span>
                                    {t('app.dirs.whatIs.mid')}
                                    <span style={{ color: c.text }}>{t('app.dirs.whatIs.lead2')}</span>
                                    {t('app.dirs.whatIs.tail')}
                                </Muted>
                            </Panel>

                            {weakest && weakestDir && (
                                <Panel title={t('app.dirs.weakest')} pad="16px 18px 18px">
                                    <Link to={R.dir(weakest.code)} style={{ textDecoration: 'none' }}>
                                        <Col gap={12}>
                                            <Row style={{ gap: 11 }}>
                                                <Icon
                                                    k={weakest.code}
                                                    size={16}
                                                    color={scoreColor(weakest.score).hex}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 15,
                                                        color: c.textPri,
                                                        fontWeight: 400,
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {weakestDir.disp}
                                                </span>
                                                <span
                                                    style={{
                                                        fontFamily: f.mono,
                                                        fontSize: 17,
                                                        fontWeight: 300,
                                                        color: c.textPri,
                                                    }}
                                                >
                                                    {weakest.score}
                                                </span>
                                            </Row>
                                            <SegBarC
                                                pct={weakest.score || 0}
                                                hex={scoreColor(weakest.score).hex}
                                                n={12}
                                            />
                                            <Muted style={{ fontSize: 12 }}>{weakestDir.sub}</Muted>
                                            <Row style={{ gap: 7 }}>
                                                <Meta
                                                    style={{
                                                        fontSize: 9.5,
                                                        color: scoreColor(weakest.score).hex,
                                                        letterSpacing: '0.12em',
                                                    }}
                                                >
                                                    {t('app.state.openDirection')}
                                                </Meta>
                                                <Icon k="arrow" size={12} color={scoreColor(weakest.score).hex} />
                                            </Row>
                                        </Col>
                                    </Link>
                                </Panel>
                            )}
                        </Rail>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    /* ── mobile ──────────────────────────────────────── */
    return (
        <AppShell {...shellProps}>
            <Appear>
                <Col style={{ padding: '10px 20px 0' }} gap={11}>
                    <H1>{t('app.dirs.title')}</H1>
                    <Muted>{intro}</Muted>
                </Col>
                <div style={{ margin: '16px 0 0', borderTop: hair }} />

                {DIR_ORDER.map((code, i) => {
                    const d = content?.dirBy[code];
                    const st = profile.directions.find((x) => x.code === code);
                    const deep = !!st?.deep;
                    const z = scoreColor(deep ? st?.score : null);
                    const pats = st?.patterns.length || 0;

                    if (!d) return null;

                    return (
                        <Link key={code} to={R.dir(code)} style={{ textDecoration: 'none', display: 'block' }}>
                            <div style={{ padding: '15px 20px 14px', borderBottom: hair2 }}>
                                <Row style={{ gap: 13 }}>
                                    <Meta style={{ fontSize: 10, color: c.subtle, width: 16, flex: '0 0 auto' }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </Meta>
                                    <Icon
                                        k={deep ? code : 'lock'}
                                        size={deep ? 18 : 15}
                                        color={deep ? z.hex : 'rgba(232,230,224,0.3)'}
                                    />
                                    <Col style={{ flex: 1, minWidth: 0 }} gap={4}>
                                        <Row style={{ gap: 9 }}>
                                            <span
                                                style={{
                                                    fontSize: 17.5,
                                                    fontWeight: 400,
                                                    letterSpacing: '-0.022em',
                                                    color: deep ? c.textPri : c.dim,
                                                }}
                                            >
                                                {d.disp}
                                            </span>
                                            {deep && st?.delta != null && <Delta v={st.delta} />}
                                        </Row>
                                        <Muted style={{ fontSize: 12, color: deep ? c.muted : c.subtle }}>
                                            {d.sub}
                                        </Muted>
                                    </Col>
                                    <Row style={{ gap: 9, flex: '0 0 auto' }}>
                                        {deep ? (
                                            <span
                                                style={{
                                                    fontFamily: f.mono,
                                                    fontSize: 23,
                                                    fontWeight: 300,
                                                    letterSpacing: '-0.03em',
                                                    color: c.textPri,
                                                }}
                                            >
                                                {st?.score}
                                            </span>
                                        ) : (
                                            <Meta style={{ fontSize: 10, color: c.subtle }}>
                                                {t('app.zone.locked')}
                                            </Meta>
                                        )}
                                        <Icon k="chev" size={13} color={c.subtle} />
                                    </Row>
                                </Row>
                                <Col style={{ margin: '11px 0 0 29px' }} gap={7}>
                                    <Row style={{ gap: 11 }}>
                                        {deep ? <SegBarC pct={st?.score || 0} hex={z.hex} n={12} /> : <SegBarEmpty />}
                                    </Row>
                                    <Row style={{ justifyContent: 'space-between' }}>
                                        <Meta
                                            style={{
                                                fontSize: 9.5,
                                                letterSpacing: '0.12em',
                                                color: deep ? z.hex : c.subtle,
                                            }}
                                        >
                                            {deep ? t(z.key) : t('app.dirs.sixMinutes')}
                                        </Meta>
                                        {(deep || !!st?.earned) && (
                                            <Meta style={{ fontSize: 9, letterSpacing: '0.1em', color: c.subtle }}>
                                                {deep && tn('app.state.patternCount', pats)}
                                                {!!st?.earned && (
                                                    <span style={{ color: ACT }}>
                                                        {deep ? ' · ' : ''}+{tn('app.state.points', st.earned)}
                                                    </span>
                                                )}
                                            </Meta>
                                        )}
                                    </Row>
                                </Col>
                            </div>
                        </Link>
                    );
                })}

                <Muted style={{ fontSize: 12, padding: '16px 20px 24px' }}>{outro}</Muted>
            </Appear>
        </AppShell>
    );
};

export default DirectionsPage;
